/**
 * Connection Store Actions
 * Action implementations for the connection store
 */

import { StoreApi } from 'zustand';
import { ConnectionState } from './types';
import { connectionApi } from './api';
import { ConnectionRetryManager } from '../../utils/connectionRetry';
import { CompanyFetchCoordinator } from '../../services/companyFetchCoordinator';

// Dashboard initialization tracker
let dashboardInitialized = false;
let dashboardInitPromise: Promise<void> | null = null;

export const createConnectionActions = (
  set: StoreApi<ConnectionState>['setState'],
  get: StoreApi<ConnectionState>['getState']
) => ({
  initializeDashboard: async () => {
    if (dashboardInitialized && !dashboardInitPromise) {
      return; // Already initialized
    }

    if (dashboardInitPromise) {
      return dashboardInitPromise; // Return existing promise to prevent duplicate calls
    }

    dashboardInitPromise = (async () => {
      try {
        const currentState = get();
        if (currentState.isInitialized) {
          return; // Another component already initialized
        }

        set({ isLoading: true, error: null });

        // Single connection check for dashboard initialization
        await get().checkConnection(false);

        // Fetch companies during dashboard initialization if connected
        const postCheckState = get();
        if (postCheckState.status === 'connected' && postCheckState.companies.length === 0) {
          // Use coordinated fetch for dashboard initialization - this is the ONLY auto-fetch
          await CompanyFetchCoordinator.ensureCompaniesFetched(false, 'dashboard-init');
        }

        set({ isInitialized: true, isLoading: false });
        dashboardInitialized = true;
      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        set({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Initialization failed'
        });
        throw error;
      } finally {
        dashboardInitPromise = null;
      }
    })();

    return dashboardInitPromise;
  },

  checkConnection: async (forceRefresh = false) => {
    const currentState = get();

    try {
      // Don't set loading state if check is already in progress and not forced
      const checkInProgress = connectionApi.checkStatus.toString().includes('connectionCheckInProgress');
      if (!checkInProgress || forceRefresh) {
        set({ isLoading: true, error: null });
      }

      // Use retry mechanism for connection checks
      const retryResult = await ConnectionRetryManager.checkConnectionWithRetry(
        () => connectionApi.checkStatus(forceRefresh),
        {
          maxRetries: forceRefresh ? 1 : 2, // More retries for automatic checks
          initialDelay: 2000,
          maxDelay: 8000
        }
      );

      if (retryResult.success && retryResult.data) {
        const connectionData = retryResult.data;

        // Determine if connection is healthy based on status and recent failures
        const isHealthy = connectionData.status === 'connected' && currentState.consecutiveFailures < 3;

        const newState = {
          status: connectionData.status,
          lastConnected: connectionData.lastConnected,
          deviceName: connectionData.deviceName,
          tallyVersion: connectionData.tallyVersion,
          companyName: connectionData.companyName,
          connectionId: connectionData.connectionId,
          lastChecked: new Date(),
          consecutiveFailures: connectionData.status === 'connected' ? 0 : currentState.consecutiveFailures + 1,
          isHealthy,
          isLoading: false,
          error: null,
        };

        set(newState);

        // Note: Auto-fetch companies removed from here to prevent duplicate calls
        // Companies are now fetched only during dashboard initialization or manual refresh
      } else {
        throw retryResult.error || new Error('Connection check failed after retries');
      }
    } catch (error) {
      // Invalidate cache on error
      connectionApi.invalidateCache();

      // Don't mask the error - let it bubble up
      set({
        lastChecked: new Date(),
        consecutiveFailures: currentState.consecutiveFailures + 1,
        isHealthy: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Connection check failed',
      });

      // Re-throw to expose the real error
      throw error;
    }
  },

  refreshWithCompanies: async () => {
    let attemptCount = 0;
    const maxAttempts = 3; // Increased from 2 to 3 attempts

    set({ isLoading: true, error: null });

    while (attemptCount < maxAttempts) {
      try {
        attemptCount++;

        const refreshData = await connectionApi.refresh();

        set({
          companies: refreshData.companies,
          isLoading: false,
          error: null,
        });

        // If we got companies and don't have a selected company, select the first one
        if (refreshData.companies.length > 0 && !get().selectedCompany) {
          const firstCompany = refreshData.companies[0].name;
          set({ selectedCompany: firstCompany });
          // Store in localStorage for persistence
          localStorage.setItem('selectedTallyCompany', firstCompany);
        }

        return; // Success - exit the function

      } catch (error) {
        console.error(`[RefreshCompanies] Attempt ${attemptCount} failed:`, error);

        // Check if this is a network/connection error that might be recoverable
        const errorMessage = error instanceof Error ? error.message : 'Connection failed';
        const isNetworkError = errorMessage.includes('fetch') ||
                               errorMessage.includes('network') ||
                               errorMessage.includes('HTTP') ||
                               errorMessage.includes('timeout');

        // If this was the final attempt, show detailed error message
        if (attemptCount >= maxAttempts) {
          const userFriendlyError = `Tally is not responding after ${maxAttempts} attempts.

Possible reasons:
• Tally application not running
• Tally Connector not installed/running
• Network connectivity issues
• Firewall blocking connection

Correction steps:
• Ensure Tally is open and running
• Check if Tally Connector is active
• Verify network connection
• Check firewall settings

Technical error: ${errorMessage}`;

          set({
            isLoading: false,
            error: userFriendlyError,
          });
          throw error;
        }

        // Use exponential backoff for retries (2s, 4s, 8s)
        const retryDelay = Math.min(2000 * Math.pow(2, attemptCount - 1), 8000);

        // Wait before retry (only if not final attempt)
        if (attemptCount < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));

          // For network errors, also try to refresh the connection status first
          if (isNetworkError && attemptCount === 1) {
            try {
              await get().checkConnection(true);
            } catch (connError) {
              // Connection check also failed - continue with retry
            }
          }
        }
      }
    }
  },

  deployConnector: async () => {
    try {
      set({ isLoading: true, error: null });

      // Use real API endpoint for Phase 2
      const response = await connectionApi.deploy();

      set({
        isLoading: false,
        error: null,
      });

      return response.downloadUrl;
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to deploy connector',
      });
      throw error;
    }
  },

  disconnect: async () => {
    try {
      set({ isLoading: true, error: null });

      // Use real API endpoint for Phase 2
      await connectionApi.disconnect();

      set({
        status: 'not_connected',
        lastConnected: get().lastConnected, // Keep last connected time
        deviceName: null,
        tallyVersion: null,
        companyName: null,
        connectionId: null,
        companies: [],
        selectedCompany: null,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to disconnect',
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  invalidateCache: () => {
    connectionApi.invalidateCache();
  },

  startHealthMonitoring: () => {
    const currentState = get();
    if (currentState._healthMonitorInterval) return; // Already running

    const interval = setInterval(() => {
      // Check connection every 60 seconds (increased from 30s to reduce API calls)
      // Use non-force refresh to respect caching
      get().checkConnection(false);
    }, 60000);

    set({ _healthMonitorInterval: interval });
  },

  stopHealthMonitoring: () => {
    const currentState = get();
    if (currentState._healthMonitorInterval) {
      clearInterval(currentState._healthMonitorInterval);
      set({ _healthMonitorInterval: null });
    }
  },
});
